import { useCallback, useMemo } from 'react';
import { useNavigation, useRoute } from '@react-navigation/native';

import {
  metricsFiltersToRouteParams,
  parseMetricsRouteParams,
  resolveMetricsReportFilters,
  routeParamsAfterFilterChange,
  type MetricsReportFilterKey,
  type MetricsReportFilters,
} from './reportFilterDefaults';

export type ReportFilterRouteParams = Partial<MetricsReportFilters> & Record<string, unknown>;

/**
 * Keeps report filter changes in route params (URL on web). Only non-default values are stored.
 */
export function useReportFilters() {
  const route = useRoute();
  const navigation = useNavigation();

  const routeOverrides = useMemo(
    () => parseMetricsRouteParams(route.params as Record<string, unknown> | undefined),
    [route.params],
  );

  const appliedFilters = useMemo(
    () => resolveMetricsReportFilters(routeOverrides),
    [routeOverrides],
  );

  const setFilter = useCallback(
    <K extends MetricsReportFilterKey>(name: K, value: MetricsReportFilters[K]) => {
      const params = routeParamsAfterFilterChange(appliedFilters, name, value);
      // The admin drawer screen is untyped, so setParams is declared to take undefined.
      (navigation as { setParams: (params: ReportFilterRouteParams) => void }).setParams(params);
    },
    [appliedFilters, navigation],
  );

  const resetFilters = useCallback(() => {
    (navigation as { setParams: (params: ReportFilterRouteParams) => void }).setParams({
      gated: undefined,
      from: undefined,
      to: undefined,
      excludePasteHeavy: undefined,
      excludeFirstTryPasses: undefined,
    });
  }, [navigation]);

  const urlParamKeys = useMemo(
    () => Object.keys(metricsFiltersToRouteParams(appliedFilters)),
    [appliedFilters],
  );

  return {
    appliedFilters,
    setFilter,
    resetFilters,
    urlParamKeys,
  };
}
